(() => {
  'use strict';

  /* Arabic, Persian and Urdu presentation ranges. */
  const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u0870-\u089F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/u;
  const TEXT_CLASS = 'vertu-arabic-font';
  const ATTRIBUTE_CLASS = 'vertu-arabic-attribute-font';
  const SHADOW_STYLE_ID = 'vertu-arabic-font-shadow-style';
  const SHADOW_STYLE_TEXT = `
    .${TEXT_CLASS}, .${ATTRIBUTE_CLASS},
    input.${ATTRIBUTE_CLASS}::placeholder,
    textarea.${ATTRIBUTE_CLASS}::placeholder {
      font-family: 'Vertu Bahij TheSansArabic', 'Tenor Sans', sans-serif !important;
      font-style: normal !important;
      font-synthesis: none !important;
      font-variant-ligatures: common-ligatures contextual !important;
      font-feature-settings: 'liga' 1, 'calt' 1 !important;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
    }
  `;
  const ATTRIBUTES = ['placeholder', 'value', 'title', 'aria-label', 'alt'];
  const SKIP_SELECTOR = [
    'script',
    'style',
    'noscript',
    'textarea',
    'option',
    'select',
    'svg',
    'math',
    'canvas',
    'template',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    `.${TEXT_CLASS}`
  ].join(',');

  const observedRoots = new WeakSet();

  const hasArabic = (value) => typeof value === 'string' && ARABIC_RE.test(value);

  const isSkippedTextNode = (textNode) => {
    const parent = textNode.parentElement;
    return !parent || Boolean(parent.closest(SKIP_SELECTOR));
  };

  /* Wrap only whitespace-separated tokens containing Arabic script. English
     tokens in mixed-language strings remain untouched and keep their existing
     theme font. Inline spans preserve wrapping and all surrounding layout. */
  const processTextNode = (textNode) => {
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;

    const value = textNode.nodeValue || '';
    if (!hasArabic(value) || isSkippedTextNode(textNode)) return;

    const parts = value.split(/(\s+)/u);
    const fragment = textNode.ownerDocument.createDocumentFragment();
    let changed = false;

    for (const part of parts) {
      if (part && hasArabic(part)) {
        const span = textNode.ownerDocument.createElement('span');
        span.className = TEXT_CLASS;
        span.textContent = part;
        fragment.appendChild(span);
        changed = true;
      } else {
        fragment.appendChild(textNode.ownerDocument.createTextNode(part));
      }
    }

    if (changed && textNode.parentNode) {
      textNode.parentNode.replaceChild(fragment, textNode);
    }
  };

  const elementHasArabicAttribute = (element) => {
    if (!(element instanceof Element)) return false;

    for (const name of ATTRIBUTES) {
      if (hasArabic(element.getAttribute(name) || '')) return true;
    }

    if (
      (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
      hasArabic(element.value || '')
    ) {
      return true;
    }

    return false;
  };

  const processElementAttributes = (element) => {
    if (!(element instanceof Element)) return;
    element.classList.toggle(ATTRIBUTE_CLASS, elementHasArabicAttribute(element));
  };

  const ensureShadowStyle = (root) => {
    if (!(root instanceof ShadowRoot) || root.getElementById(SHADOW_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SHADOW_STYLE_ID;
    style.textContent = SHADOW_STYLE_TEXT;
    root.prepend(style);
  };

  const processRoot = (root) => {
    ensureShadowStyle(root);
    if (!root) return;

    if (root instanceof Element) {
      processElementAttributes(root);
    }

    const elementWalker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
    let element = elementWalker.nextNode();

    while (element) {
      processElementAttributes(element);

      if (element.shadowRoot) {
        processRoot(element.shadowRoot);
        observeRoot(element.shadowRoot);
      }

      element = elementWalker.nextNode();
    }

    const textWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let textNode = textWalker.nextNode();

    /* Collect first because replacing nodes while walking can skip siblings in
       Safari/WebKit, especially inside review and accordion widgets. */
    while (textNode) {
      textNodes.push(textNode);
      textNode = textWalker.nextNode();
    }

    textNodes.forEach(processTextNode);
  };

  const observeRoot = (root) => {
    if (!root || observedRoots.has(root)) return;
    observedRoots.add(root);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          processTextNode(mutation.target);
          continue;
        }

        if (mutation.type === 'attributes') {
          processElementAttributes(mutation.target);
          continue;
        }

        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.TEXT_NODE) {
            processTextNode(node);
          } else if (node instanceof Element || node instanceof DocumentFragment) {
            processRoot(node);
          }
        }
      }
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ATTRIBUTES
    });
  };

  /* Review apps and other Shopify app blocks can attach an open Shadow DOM
     after page load. Hook it so Arabic text is styled there immediately too. */
  const patchAttachShadow = () => {
    if (!Element.prototype.attachShadow || Element.prototype.attachShadow.__vertuArabicPatched) return;

    const nativeAttachShadow = Element.prototype.attachShadow;
    const patchedAttachShadow = function(init) {
      const shadowRoot = nativeAttachShadow.call(this, init);
      Promise.resolve().then(() => {
        processRoot(shadowRoot);
        observeRoot(shadowRoot);
      });
      return shadowRoot;
    };

    patchedAttachShadow.__vertuArabicPatched = true;

    try {
      Element.prototype.attachShadow = patchedAttachShadow;
    } catch (error) {
      /* Some embedded browsers expose a non-writable prototype. Existing and
         subsequently inserted open roots are still covered by normal scans. */
    }
  };

  const rescan = (event) => {
    const target = event && event.target instanceof Element ? event.target : document.body;
    if (target) processRoot(target);
  };

  const start = () => {
    patchAttachShadow();
    processRoot(document.body);
    observeRoot(document.body);

    document.addEventListener('input', (event) => {
      if (event.target instanceof Element) processElementAttributes(event.target);
    }, true);

    document.addEventListener('shopify:section:load', rescan);
    document.addEventListener('shopify:block:select', rescan);
    window.addEventListener('pageshow', rescan);
    window.addEventListener('load', rescan, { once: true });

    /* Extra delayed passes cover app widgets that replace their complete DOM
       during hydration without leaving a stable container. MutationObserver
       remains active after these passes. */
    [250, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(() => processRoot(document.body), delay);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
